import React, { useEffect, useState } from 'react';
import api from '../../api'; // Ensure path is correct based on your folder structure
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subtotal, setSubtotal] = useState(0);
  
  // Shipping State
  const [shippingTotal, setShippingTotal] = useState(0);

  // --- COUPON STATE ---
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  
  // --- ADDRESS STATE ---
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [newAddress, setNewAddress] = useState({
      name: '', mobile: '', houseName: '', street: '', city: '', state: '', pinCode: ''
  });

  const history = useHistory();
  const userId = localStorage.getItem("userId");

  // Final Amount Formula
  const finalAmount = subtotal + shippingTotal - discount;
  const MIN_ORDER_VALUE = 300; 

  // --- RAZORPAY LOADER ---
  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // --- DATA FETCHING ---
  const fetchCartItems = async () => {
    if (!userId || userId === "0") {
      setLoading(false);
      return;
    }
    try {
      const response = await api.post(`/usercart/in-cart?userId=${userId}&pageSize=1000`);
      const items = response.data || [];
      setCartItems(items);
      calculateTotal(items);
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCartItems();
  }, [userId]);

  // Calculate Shipping
  const calculateTotal = (items) => {
    let total = 0;
    let shipping = 0;

    items.forEach(item => {
      const price = item.productRate || 0;
      const qty = parseInt(item.orderQuantity) || 1;
      
      const itemShipping = item.shippingCharge || (item.productsObject && item.productsObject.shippingCharge) || 0;

      total += price * qty;
      shipping += itemShipping * qty; 
    });

    setSubtotal(total);
    setShippingTotal(shipping);
    
    // Reset discount on cart update
    if (appliedCoupon) {
       // Ideally re-validate, but for simplicity we reset if cart changes significantly
       // Or you can call applyCoupon() again here. 
       // For now, keeping your existing logic:
       setDiscount(0);
       setAppliedCoupon(null);
       setCouponCode('');
    }
  };

  // --- CART OPERATIONS ---
  const updateQuantity = async (productId, currentQty, change) => {
    const newQty = parseInt(currentQty) + change;
    if (newQty < 1) return;
    try {
      const updatedItems = cartItems.map(item =>
        item.productid === productId ? { ...item, orderQuantity: newQty } : item
      );
      setCartItems(updatedItems);
      calculateTotal(updatedItems);
      await api.post(`/usercart/update-quantity`, null, {
        params: { userId, productId, orderQuantity: newQty }
      });
    } catch (error) {
      console.error("Error updating quantity:", error);
      fetchCartItems();
    }
  };

  const removeItem = async (productId) => {
    try {
      const updatedItems = cartItems.filter(item => item.productid !== productId);
      setCartItems(updatedItems);
      calculateTotal(updatedItems);
      await api.post(`/usercart/remove-product`, null, {
        params: { userId, productId }
      });
    } catch (error) {
      console.error("Error removing item:", error);
      fetchCartItems();
    }
  };

  // --- COUPON LOGIC (UPDATED) ---
  const applyCoupon = async () => {
      if(!couponCode) {
          toast.warning("Enter a code first");
          return;
      }
      try {
          // ➤ UPDATED: Sending userId along with code and amount
          const res = await api.post('/api/coupons/validate', {
              code: couponCode,
              amount: subtotal,
              userId: userId // <--- Added userId here
          });
          
          const { discountAmount } = res.data;
          setDiscount(discountAmount);
          setAppliedCoupon(couponCode);
          toast.success(`Saved ₹${discountAmount}!`);
      } catch (err) {
          const msg = err.response?.data || "Invalid Coupon";
          toast.error(msg);
          setDiscount(0);
          setAppliedCoupon(null);
      }
  };

  const removeCoupon = () => {
      setDiscount(0);
      setAppliedCoupon(null);
      setCouponCode('');
      toast.info("Coupon removed");
  };

  // --- CHECKOUT LOGIC ---
  const initiateBuyProcess = async () => {
      if (finalAmount < MIN_ORDER_VALUE) {
        toast.error(`Minimum order value is ₹${MIN_ORDER_VALUE}. Please add more items.`);
        return;
      }

      if (!userId || userId === "0") {
          toast.info("Please login to checkout");
          return;
      }
      try {
          const res = await api.get(`/addresses/user/${userId}`);
          const userAddresses = res.data || [];
          setAddresses(userAddresses);
          
          if(userAddresses.length > 0) {
              setSelectedAddressId(userAddresses[0].addrId);
              setViewMode('list');
          } else {
              setViewMode('add');
          }
          setShowAddressModal(true);
      } catch(err) {
          console.error("Error fetching addresses", err);
          toast.error("Could not load addresses");
      }
  };

  const handleSaveAddress = async (e) => {
      e.preventDefault();
      try {
          const res = await api.post(`/addresses/add?userId=${userId}`, newAddress);
          setAddresses([...addresses, res.data]);
          setSelectedAddressId(res.data.addrId);
          setViewMode('list');
          toast.success("Address Saved!");
      } catch(err) {
          toast.error("Failed to save address");
      }
  };

  const handleInputChange = (e) => {
      setNewAddress({...newAddress, [e.target.name]: e.target.value});
  };

  // ➤ Helper: Format Address Object to String for Backend Storage
  const formatAddressToString = (addr) => {
      if(!addr) return "";
      // Format: ID@Name@Mobile@House@Street@Landmark@City@State@Pin@Country@Type@CountryCode
      // We use safe checks (|| "") to avoid 'undefined' in string
      return `${addr.addrId}@${addr.name}@${addr.mobile}@${addr.houseName || ""}@${addr.street || ""}@${addr.landMark || ""}@${addr.city}@${addr.state}@${addr.pinCode}@India@Home@91`;
  };

  const handleFinalCheckout = async () => {
    if(!selectedAddressId) {
        toast.warning("Please select a delivery address.");
        return;
    }
    
    // ➤ Find the full address object
    const selectedAddressObj = addresses.find(a => a.addrId === selectedAddressId);
    if (!selectedAddressObj) {
        toast.error("Selected address not found.");
        return;
    }

    // ➤ Create the formatted string
    const addressString = formatAddressToString(selectedAddressObj);

    setShowAddressModal(false);
    const res = await loadRazorpay();
    if (!res) {
      toast.error("Razorpay SDK failed to load.");
      return;
    }

    try {
      const result = await api.post('/orders/create-transaction', { amount: finalAmount });
      const { orderId, key } = result.data;

      const options = {
        key: key, 
        amount: finalAmount * 100,
        currency: "INR",
        name: "Divine Depot",
        description: "Order Payment",
        order_id: orderId,
        handler: async function (response) {
          try {
            await api.post('/orders/place', null, {
              params: {
                userId,
                paymentMethod: "ONLINE",
                addressId: addressString, // ➤ SEND FULL STRING INSTEAD OF JUST ID
                amount: finalAmount,
                couponCode: appliedCoupon || ""
              }
            });
            toast.success("Order Placed Successfully!");
            setCartItems([]);
            setSubtotal(0);
            setShippingTotal(0); 
            setDiscount(0);
            history.push('/orders'); 
          } catch (err) {
            console.error("Order Placement Failed", err);
            toast.error("Payment succeeded but order creation failed.");
          }
        },
        theme: { color: "#FFC35B" },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();

    } catch (err) {
      console.error("Checkout Error:", err);
      toast.error("Could not initiate payment.");
    }
  };

  if (loading) return <div className="text-center py-5">Loading Cart...</div>;

  return (
    <div style={{ backgroundColor: "#EAEDED", minHeight: "100vh", paddingBottom: "50px" }}>
      <div className="container py-4">
        
        {/* --- PAGE HEADER --- */}
        <div className="d-flex align-items-center justify-content-between mb-3">
             <h2 style={{ fontSize: "28px", fontWeight: "400" }}>Shopping Cart</h2>
             {cartItems.length > 0 && <span className="text-muted">Price</span>}
        </div>

        {cartItems.length === 0 ? (
           <div className="card p-5 text-center shadow-sm">
             <h2 className="mb-3">Your Divine Depot Cart is empty.</h2>
             <p>Check your Saved for later items below or continue shopping.</p>
             <button className="btn btn-warning mt-2" onClick={() => history.push('/our-shop')}>Continue Shopping</button>
           </div>
        ) : (
          <div className="row">
            
            {/* --- LEFT COLUMN: CART ITEMS --- */}
            <div className="col-lg-9">
                <div className="card shadow-sm border-0 mb-3">
                    <div className="card-body p-0">
                        {cartItems.map((item, index) => {
                            const product = {
                                name: item.productName || "Unknown Product",
                                price: item.productRate || 0,
                                image: item.productImage || "https://via.placeholder.com/150",
                                inStock: true 
                            };
                            return (
                                <div key={item.id}>
                                    <div className="row p-3 align-items-center">
                                        {/* Image */}
                                        <div className="col-3 col-md-2 text-center">
                                            <img src={product.image} alt={product.name} className="img-fluid" style={{ maxHeight: '120px', objectFit: 'contain' }} onError={(e) => e.target.src = "https://via.placeholder.com/150"} />
                                        </div>
                                        
                                        {/* Details */}
                                        <div className="col-9 col-md-7">
                                            <h5 className="mb-1" style={{ fontSize: "18px", fontWeight: "500" }}>{product.name}</h5>
                                            <div className="small text-success mb-2">In Stock</div>
                                            
                                            <div className="d-flex align-items-center gap-2">
                                                {/* Quantity Selector */}
                                                <div className="input-group input-group-sm" style={{ width: '110px' }}>
                                                    <button className="btn btn-light border" onClick={() => updateQuantity(item.productid, item.orderQuantity, -1)}>-</button>
                                                    <span className="form-control text-center bg-white">{item.orderQuantity}</span>
                                                    <button className="btn btn-light border" onClick={() => updateQuantity(item.productid, item.orderQuantity, 1)}>+</button>
                                                </div>
                                                <div className="vr mx-2"></div>
                                                <span className="text-primary small" style={{ cursor: 'pointer', textDecoration: 'none' }} onClick={() => removeItem(item.productid)}>
                                                    Delete
                                                </span>
                                            </div>
                                        </div>

                                        {/* Price */}
                                        <div className="col-12 col-md-3 text-end mt-2 mt-md-0">
                                            <h5 className="fw-bold">₹{(product.price * item.orderQuantity).toFixed(2)}</h5>
                                        </div>
                                    </div>
                                    {index < cartItems.length - 1 && <hr className="my-0 mx-3" />}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* --- RIGHT COLUMN: CHECKOUT SUMMARY --- */}
            <div className="col-lg-3">
                <div className="card shadow-sm border-0 position-sticky" style={{ top: '20px' }}>
                    <div className="card-body">
                        {/* Subtotal Section */}
                        <div className="mb-2 d-flex justify-content-between">
                            <span style={{ fontSize: "16px" }}>Subtotal: </span>
                            <span className="fw-bold">₹{subtotal.toFixed(2)}</span>
                        </div>

                        {/* Shipping Section */}
                        <div className="mb-3 d-flex justify-content-between">
                            <span style={{ fontSize: "16px" }}>Shipping: </span>
                            <span className="fw-bold text-success">
                                {shippingTotal > 0 ? `+ ₹${shippingTotal.toFixed(2)}` : 'FREE'}
                            </span>
                        </div>

                        {/* Discount Section */}
                        {discount > 0 && (
                            <div className="mb-3 text-success d-flex justify-content-between">
                                <span>Discount:</span>
                                <span>- ₹{discount.toFixed(2)}</span>
                            </div>
                        )}

                        {/* Total Section */}
                         <div className="d-flex justify-content-between border-top pt-2 mb-3">
                            <h5 className="fw-bold text-danger">Total:</h5>
                            <h5 className="fw-bold text-danger">₹{finalAmount.toFixed(2)}</h5>
                        </div>

                        {/* Minimum Order Warning */}
                        {finalAmount < MIN_ORDER_VALUE && (
                          <div className="alert alert-danger p-2 text-center" style={{ fontSize: '13px' }}>
                             Add items worth <strong>₹{(MIN_ORDER_VALUE - finalAmount).toFixed(2)}</strong> more to checkout.
                          </div>
                        )}

                        {/* Proceed Button */}
                        <button 
                            className="btn w-100 mb-3 shadow-sm"
                            onClick={initiateBuyProcess}
                            disabled={finalAmount < MIN_ORDER_VALUE}
                            style={{ 
                              backgroundColor: finalAmount < MIN_ORDER_VALUE ? '#e0e0e0' : '#FFD814', 
                              borderColor: finalAmount < MIN_ORDER_VALUE ? '#d0d0d0' : '#FCD200', 
                              borderRadius: '8px', 
                              color: finalAmount < MIN_ORDER_VALUE ? '#888' : '#0F1111',
                              cursor: finalAmount < MIN_ORDER_VALUE ? 'not-allowed' : 'pointer'
                            }}
                        >
                            Proceed to Buy
                        </button>

                        {/* Coupon Section (Compact) */}
                        <div className="bg-light p-2 rounded border">
                            <label className="small fw-bold mb-1 text-muted">Add Coupon:</label>
                            <div className="input-group input-group-sm">
                                <input 
                                    type="text" 
                                    className="form-control" 
                                    placeholder="Code"
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                    disabled={appliedCoupon}
                                    style={{ textTransform: 'uppercase' }}
                                />
                                {appliedCoupon ? (
                                    <button className="btn btn-outline-danger" onClick={removeCoupon}>✕</button>
                                ) : (
                                    <button className="btn btn-dark" onClick={applyCoupon}>Apply</button>
                                )}
                            </div>
                            {appliedCoupon && <div className="text-success mt-1" style={{ fontSize: '11px' }}>Code applied!</div>}
                        </div>

                    </div>
                </div>
            </div>

          </div>
        )}
      </div>

      {/* --- ADDRESS MODAL --- */}
      {showAddressModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content shadow">
                    <div className="modal-header bg-light">
                        <h5 className="modal-title">Select Delivery Address</h5>
                        <button type="button" className="btn-close" onClick={() => setShowAddressModal(false)}></button>
                    </div>
                    <div className="modal-body p-4">
                        
                        {viewMode === 'list' && (
                            <div>
                                <div className="row">
                                    {addresses.map(addr => (
                                        <div key={addr.addrId} className="col-md-6 mb-3">
                                            <div 
                                                className={`card h-100 p-3 ${selectedAddressId === addr.addrId ? 'border-primary bg-light' : ''}`} 
                                                onClick={() => setSelectedAddressId(addr.addrId)} 
                                                style={{ cursor: 'pointer', borderWidth: selectedAddressId === addr.addrId ? '2px' : '1px' }}
                                            >
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" checked={selectedAddressId === addr.addrId} readOnly />
                                                    <label className="form-check-label ps-2">
                                                        <strong className="d-block">{addr.name}</strong>
                                                        <span className="small text-muted">{addr.houseName}, {addr.street}</span><br/>
                                                        <span className="small text-muted">{addr.city}, {addr.state} - {addr.pinCode}</span><br/>
                                                        <span className="small text-muted">Phone: {addr.mobile}</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                
                                <button className="btn btn-outline-primary btn-sm mt-2" onClick={() => setViewMode('add')}>
                                    + Add New Address
                                </button>

                                <hr />
                                <div className="text-end">
                                    <button className="btn btn-link text-decoration-none text-muted me-3" onClick={() => setShowAddressModal(false)}>Cancel</button>
                                    <button className="btn btn-warning shadow-sm" onClick={handleFinalCheckout}>
                                        Pay ₹{finalAmount.toFixed(2)}
                                    </button>
                                </div>
                            </div>
                        )}

                        {viewMode === 'add' && (
                            <form onSubmit={handleSaveAddress}>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label small">Full Name</label>
                                        <input type="text" className="form-control" name="name" required onChange={handleInputChange} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label small">Mobile Number</label>
                                        <input type="text" className="form-control" name="mobile" required onChange={handleInputChange} />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label small">Flat, House no., Building, Company, Apartment</label>
                                        <input type="text" className="form-control" name="houseName" required onChange={handleInputChange} />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label small">Area, Street, Sector, Village</label>
                                        <input type="text" className="form-control" name="street" required onChange={handleInputChange} />
                                    </div>
                                    <div className="col-md-5">
                                        <label className="form-label small">Town/City</label>
                                        <input type="text" className="form-control" name="city" required onChange={handleInputChange} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label small">State</label>
                                        <input type="text" className="form-control" name="state" required onChange={handleInputChange} />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label small">Pincode</label>
                                        <input type="text" className="form-control" name="pinCode" required onChange={handleInputChange} />
                                    </div>
                                </div>
                                <div className="mt-4 d-flex justify-content-between">
                                    <button type="button" className="btn btn-light btn-sm" onClick={() => setViewMode('list')}>Back</button>
                                    <button type="submit" className="btn btn-warning btn-sm">Use this address</button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default Cart;